<template>
  <div>
    <opening-screen v-if="showOpening" @done="showOpening = false" />
    <div class="wrapper">
      <header class="mobile-app-bar">
        <router-link :to="logoRoute" class="mobile-app-bar__brand">
          <img :src="brandIcon" alt="Mantraz" class="mobile-app-bar__brand-icon" />
        </router-link>
        <h1 class="mobile-app-bar__title">{{ pageTitle }}</h1>
        <div class="mobile-app-bar__actions">
          <navbar-notifications v-if="isMobileShell && canRead('notification_inbox')" />
          <img :src="profileImage" alt="" class="mobile-app-bar__avatar mz-nav-icon" />
        </div>
      </header>

      <b-navbar toggleable="lg" :type="$store.state.theme.resolved">
        <b-navbar-brand :to="logoRoute">
          <img :src="logo" alt="Logo" class="d-inline-block align-top">
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <b-navbar-nav v-if="activeApp?.name === 'fms'">
            <b-nav-item
              v-for="item in fmsNavItems"
              :key="item.key"
              :to="item.to"
              :href="item.href"
              :class="{ 'selected': $route.name === item.selectedWhen }"
              class="d-flex align-items-center">
              <img :src="iconFor(item)" :class="['mr-2', 'mz-nav-icon', { 'is-active': iconIsActive(item) }]" />
              <span :class="{ 'pink-text': $route.name === (item.labelActiveWhen || item.selectedWhen) }">{{ item.label }}</span>
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav v-if="activeApp?.name === 'bunkering'">
            <b-nav-item
              v-for="item in bunkeringNavItems"
              :key="item.key"
              :to="item.to"
              :class="{ 'selected': $route.name === item.selectedWhen }"
              class="d-flex align-items-center">
              <img :src="iconFor(item)" :class="['mr-2', 'mz-nav-icon', { 'is-active': iconIsActive(item) }]" />
              <span :class="{ 'pink-text': $route.name === item.selectedWhen }">{{ item.label }}</span>
            </b-nav-item>
          </b-navbar-nav>

          <b-navbar-nav class="navbar-actions ml-auto d-flex align-items-center">

            <!-- Select Company and App Dropdown -->
            <b-nav-item v-if="hideCompanySelection" v-on:click="openSelectAppModal"
              class="d-flex align-items-center">
              <img :src="icon.IconApp" alt="App Icon" class="mr-2 mz-nav-icon" style="width: 18px; height: 18px;">
              <span>Select Application</span>
            </b-nav-item>
            <b-nav-item-dropdown v-else text="Options" right>
              <template #button-content>
                <img :src="icon.IconSelection" class="mr-2 mz-nav-icon" style="width: 18px; height: 18px;">
                <span>Select App/Company</span>
              </template>
              <b-dropdown-item v-on:click="openSelectAppModal">
                <img :src="icon.IconApp" alt="App Icon" class="mr-2 mz-nav-icon" style="width: 22px; height: 22px;">
                Select Application
              </b-dropdown-item>
              <b-dropdown-item v-on:click="openSelectCompanyModal">
                <img :src="icon.IconCompany" alt="Company Icon" class="mr-2 mz-nav-icon">
                Select Company
              </b-dropdown-item>
            </b-nav-item-dropdown>
            <!--/ Select Company and App Dropdown -->

            <navbar-notifications v-if="!isMobileShell && canRead('notification_inbox')" />

            <!-- Config Button -->
            <b-nav-item :to="{ name: 'MasterConfiguration' }"
              :class="{ 'selected': $route.name === 'MasterConfiguration' }"
              class="d-flex align-items-center navbar-action-item" v-b-tooltip.hover title="Configuration">
              <i class="ri-settings-3-line" style="font-size: 1.5rem;"
                :class="{ 'pink-text': $route.name === 'MasterConfiguration' }"></i>
            </b-nav-item>

            <!-- dropdown user -->
            <b-nav-item-dropdown class="d-flex align-items-center navbar-action-item" right>
              <!-- Using 'button-content' slot -->
              <template #button-content>
                <img :src="profileImage" class="mr-2 mz-nav-icon" :style="{ width: '25px', height: '25px' }">
                <span class="truncate">{{ userId }}</span>
              </template>
              <b-dropdown-item href="#">Profile</b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-header>Theme</b-dropdown-header>
              <theme-picker variant="dropdown" />
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="handleLogout">Sign Out</b-dropdown-item>
            </b-nav-item-dropdown>
            <!--/ dropdown user -->
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>

      <b-modal id="select-app-modal" title="Select App" centered hide-footer>
        <app-active-selection hideHeader=true />
      </b-modal>

      <b-modal id="select-company-modal" title="Select Company" centered hide-footer>
        <company-active-selection hideHeader=true disableRedirection=true />
      </b-modal>

      <div class="app-content">
        <transition name="router-anim" mode="out-in">
          <router-view :key="$store.state.remountKey" />
        </transition>
      </div>

      <mobile-tab-bar
        :nav-items="activeNavItems"
        :icons="icon"
        :hide-company-selection="hideCompanySelection"
        @select-app="openSelectAppModal"
        @select-company="openSelectCompanyModal"
        @sign-out="handleLogout" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import logoLight from '@/assets/images/brand/mantraz-logo-horizontal.svg';
import logoDark from '@/assets/images/brand/mantraz-logo-horizontal-on-dark.svg';
import brandIcon from '@/assets/images/brand/mantraz-icon.svg';
import profileImage from '@/assets/images/user/default-user.png';
// import { sofbox } from '@/config/pluginInit';

import AppActiveSelection from '@/views/auth/AppActiveSelection';
import CompanyActiveSelection from '@/views/auth/CompanyActiveSelection';
import NavbarNotifications from '@/components/navbar/NavbarNotifications';
import OpeningScreen from '@/components/auth/OpeningScreen';
import ThemePicker from '@/components/ThemePicker.vue';
import { OPENING_KEY, shouldPlayOpening } from '@/util/auth/authIntro';
import { onMediaChange } from '@/util/theme';

import MobileTabBar from '@/components/navbar/MobileTabBar.vue';
import icon from './navIcons';
import {
  FMS_NAV_ITEMS, BUNKERING_NAV_ITEMS, navIconFor, isNavIconActive,
} from './navConfig';

// samain sama breakpoint CSS mobile-app-bar/.mobile-tab-bar, biar bell notifikasi cuma polling sekali
const MOBILE_SHELL_QUERY = '(max-width: 768px)';

export default {
  name: 'AuthorizedLayout',
  components: {
    AppActiveSelection,
    CompanyActiveSelection,
    NavbarNotifications,
    OpeningScreen,
    ThemePicker,
    MobileTabBar,
  },
  computed: {
    ...mapState(['remountKey']),
    logo() {
      return this.$store.getters['theme/isDark'] ? logoDark : logoLight;
    },
    activeCompany() {
      return this.$store.getters['auth/activeCompany'];
    },
    userAccess() {
      const access = this.$store.getters['auth/userAccess'];
      return access;
    },
    userId() {
      return this.$store.getters['auth/userId'];
    },
    activeApp() {
      return this.$store.getters['auth/activeApp'];
    },
    logoRoute() {
      if (this.activeApp?.name === 'bunkering') {
        return { name: 'BunkerDashboard' };
      }

      return { name: 'Home' };
    },
    fmsNavItems() {
      return FMS_NAV_ITEMS.filter((item) => !item.requires || item.requires(this));
    },
    bunkeringNavItems() {
      return BUNKERING_NAV_ITEMS.filter((item) => !item.requires || item.requires(this));
    },
    activeNavItems() {
      return this.activeApp?.name === 'bunkering' ? this.bunkeringNavItems : this.fmsNavItems;
    },
    pageTitle() {
      const match = this.activeNavItems.find(
        (item) => this.$route.name === (item.labelActiveWhen || item.selectedWhen),
      );
      return match ? match.label : this.$route.meta?.title || 'Mantraz';
    },
  },
  data() {
    return {
      hideCompanySelection: false,
      profileImage,
      brandIcon,
      icon,
      availableCompany: [],
      showOpening: false,
      isMobileShell: false,
    };
  },
  created() {
    let pending = false;
    try {
      pending = sessionStorage.getItem(OPENING_KEY) === '1';
      sessionStorage.removeItem(OPENING_KEY);
    } catch (error) { /* mode privat */ }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.showOpening = shouldPlayOpening({ pending, reducedMotion });
  },
  mounted() {
    // sofbox.mainIndex();
    this.availableCompany = this.$store.getters['auth/availableCompany'];

    const availableCompaniesCount = Object.values(this.$store.getters['auth/availableCompany']).length;
    if (availableCompaniesCount === 1) this.hideCompanySelection = true;

    // satu bell notifikasi yang aktif polling, bukan dua (desktop + mobile bar)
    this.mobileShellMedia = window.matchMedia(MOBILE_SHELL_QUERY);
    this.isMobileShell = this.mobileShellMedia.matches;
    this.removeMobileShellListener = onMediaChange(this.mobileShellMedia, this.onMobileShellChange);
  },
  beforeDestroy() {
    if (this.removeMobileShellListener) this.removeMobileShellListener();
  },
  methods: {
    onMobileShellChange(event) {
      this.isMobileShell = event.matches;
    },
    handleLogout() {
      this.$store.dispatch('auth/logout');
      window.location.href = '/auth/login';
    },
    openSelectAppModal() {
      this.$bvModal.show('select-app-modal');
    },
    openSelectCompanyModal() {
      this.$bvModal.show('select-company-modal');
    },
    iconFor(item) {
      return navIconFor(item, this.icon, this.$route.name);
    },
    iconIsActive(item) {
      return isNavIconActive(item, this.$route.name);
    },
  },
};
</script>
<style src="./AuthorizedLayout.css"></style>
